import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { sportInfo, formatMatchTime, formatRupiah } from "../lib/api";
import PageShell from "../components/PageShell";
import BottomNav from "../components/BottomNav";
import EmptyState from "../components/EmptyState";
import { Section } from "../components/Section";
import {
  Bell, MapPin, Plus, Search as SearchIcon, Users as UsersIcon, AlertTriangle, Clock, ChevronRight, Crown,
} from "lucide-react";

const MS_MIN = 60 * 1000;
const SOON_THRESHOLD_MS = 2 * 60 * MS_MIN; // 2h

export default function Home() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [crews, setCrews] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = user.city ? { city: user.city } : {};
      const [meRes, crewsRes, suggestRes] = await Promise.all([
        api.get(`/users/${user.user_id}`),
        api.get("/teams", { params: { mine: true } }),
        api.get("/matches", { params }),
      ]);
      setHistory(meRes.data.history || []);
      setCrews(crewsRes.data || []);
      // Suggested = 3 nearby matches user has NOT joined and NOT hosting
      const myId = user.user_id;
      const filtered = (suggestRes.data || [])
        .filter((m) => m.host_id !== myId && !(m.player_ids || []).includes(myId))
        .slice(0, 3);
      setSuggested(filtered);
    } catch (err) {
      console.warn("[home] load failed:", err?.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const upcomingJoined = useMemo(() => {
    const now = Date.now();
    return history
      .filter((m) => ["OPEN", "FULL"].includes(m.status) && new Date(m.starts_at).getTime() > now)
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  }, [history]);

  const needAttention = useMemo(() => {
    if (!user) return [];
    const now = Date.now();
    const out = [];
    upcomingJoined.forEach((m) => {
      const startsAt = new Date(m.starts_at).getTime();
      const minutes = Math.max(0, Math.round((startsAt - now) / MS_MIN));
      // Starting soon (within 2h)
      if (startsAt - now <= SOON_THRESHOLD_MS) {
        out.push({ kind: "starting-soon", match: m, minutes });
        return;
      }
      // Cost > 0 and my payment is none/pending → reminder
      const myPay = (m.payments || {})[user.user_id];
      if ((m.cost_per_player || 0) > 0 && (!myPay || ["none", "pending"].includes(myPay.status))) {
        out.push({ kind: "unpaid", match: m });
      }
    });
    // Host POV: pending payment proofs to review
    history
      .filter((m) => m.host_id === user.user_id && ["OPEN", "FULL"].includes(m.status))
      .forEach((m) => {
        const pendingCount = Object.values(m.payments || {}).filter((p) => p?.status === "pending").length;
        if (pendingCount > 0) out.push({ kind: "review-payment", match: m, pendingCount });
      });
    return out.slice(0, 5);
  }, [history, upcomingJoined, user]);

  const firstName = user?.name?.split(" ")[0] || "Pemain";

  return (
    <PageShell>
      {/* Personal header */}
      <header className="px-4 pt-6 pb-2 flex items-center justify-between" data-testid="home-header">
        <div>
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider">Hai 👋</div>
          <div className="font-heading text-2xl font-black tracking-tight" data-testid="home-greeting">{firstName}</div>
          {user?.city && (
            <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {user.city}
            </div>
          )}
        </div>
        <Link
          to="/notifications"
          data-testid="header-notif-btn"
          className="h-10 w-10 rounded-full bg-secondary border border-border flex items-center justify-center hover:border-primary/40"
        >
          <Bell className="h-5 w-5" />
        </Link>
      </header>

      {/* Quick Actions */}
      <section className="px-4 mt-5" data-testid="home-quick-actions">
        <div className="grid grid-cols-3 gap-2">
          <QuickAction to="/create" icon={Plus} label="Buat Match" testid="qa-create" primary />
          <QuickAction to="/search" icon={SearchIcon} label="Cari Match" testid="qa-search" />
          <QuickAction to="/teams" icon={UsersIcon} label="Crews" testid="qa-crews" />
        </div>
      </section>

      {/* Need Attention */}
      {needAttention.length > 0 && (
        <Section title="Perlu Perhatian" subtitle="Yang penting buat lo" testid="home-need-attention">
          <div className="px-4 space-y-2">
            {needAttention.map((item, i) => <AttentionRow key={`${item.kind}-${item.match.match_id}-${i}`} item={item} />)}
          </div>
        </Section>
      )}

      {/* Continue Playing */}
      <Section
        title="Continue Playing"
        subtitle={upcomingJoined.length ? `${upcomingJoined.length} match akan datang` : "Belum ada match akan datang"}
        testid="home-continue-playing"
      >
        <div className="px-4">
          {loading && upcomingJoined.length === 0 && <Skeleton count={2} />}
          {!loading && upcomingJoined.length === 0 && (
            <EmptyState
              icon="🎯"
              title="Belum ada match"
              body="Cari match yg cocok atau buat sendiri."
              action={(
                <Link to="/search" className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary text-primary-foreground font-bold glow-cyan">
                  <SearchIcon className="h-4 w-4" /> Cari Match
                </Link>
              )}
            />
          )}
          <div className="space-y-2">
            {upcomingJoined.slice(0, 5).map((m) => (
              <UpcomingRow key={m.match_id} match={m} isHost={m.host_id === user?.user_id} />
            ))}
          </div>
        </div>
      </Section>

      {/* Your Crews */}
      {crews.length > 0 && (
        <Section
          title="Crews Lo"
          subtitle={`${crews.length} team`}
          action={<Link to="/teams" data-testid="home-crews-more" className="text-xs font-bold text-primary inline-flex items-center gap-1">Semua <ChevronRight className="h-3 w-3" /></Link>}
          testid="home-crews"
        >
          <div className="px-4 flex gap-3 overflow-x-auto scrollbar-hide">
            {crews.slice(0, 6).map((t) => <CrewChip key={t.team_id} team={t} />)}
          </div>
        </Section>
      )}

      {/* Curated suggestion (1-3 max) */}
      {suggested.length > 0 && (
        <Section
          title="Saran Buat Lo"
          subtitle="Match yg cocok di kota lo"
          action={<Link to="/search" data-testid="home-suggested-more" className="text-xs font-bold text-primary inline-flex items-center gap-1">Cari lagi <ChevronRight className="h-3 w-3" /></Link>}
          testid="home-suggested"
        >
          <div className="px-4 space-y-2">
            {suggested.map((m) => <SuggestedRow key={m.match_id} match={m} />)}
          </div>
        </Section>
      )}

      <div className="h-32" aria-hidden />
      <BottomNav />
    </PageShell>
  );
}

function QuickAction({ to, icon: Icon, label, testid, primary = false }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl px-3 py-4 border transition-colors active:scale-[0.98] ${primary ? "bg-primary text-primary-foreground border-primary glow-cyan" : "bg-card border-border hover:border-primary/40"}`}
    >
      <Icon className="h-5 w-5" strokeWidth={2.4} />
      <div className="text-[11px] font-bold uppercase tracking-wider">{label}</div>
    </Link>
  );
}

function AttentionRow({ item }) {
  const { kind, match: m, minutes, pendingCount } = item;
  const s = sportInfo(m.sport);
  const cfg = {
    "starting-soon": { color: "text-amber-400", bg: "bg-amber-400/10", border: "border-amber-400/30", icon: Clock, label: minutes <= 0 ? "Mulai sekarang" : `Mulai dalam ${minutes} menit` },
    "unpaid": { color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/30", icon: AlertTriangle, label: `Bayar ${formatRupiah(m.cost_per_player)} ke host` },
    "review-payment": { color: "text-primary", bg: "bg-primary/10", border: "border-primary/30", icon: AlertTriangle, label: `${pendingCount} bukti bayar nunggu approve` },
  }[kind];
  const Icon = cfg.icon;
  return (
    <Link
      to={`/match/${m.match_id}`}
      data-testid={`attention-${kind}-${m.match_id}`}
      className={`block rounded-2xl border ${cfg.border} ${cfg.bg} p-3 flex items-center gap-3 hover:opacity-90`}
    >
      <div className={`h-9 w-9 rounded-xl bg-black/30 flex items-center justify-center ${cfg.color}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-[10px] font-bold uppercase tracking-wider ${cfg.color}`}>{cfg.label}</div>
        <div className="font-bold text-sm truncate">{s.label} · {m.venue}</div>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </Link>
  );
}

function UpcomingRow({ match: m, isHost }) {
  const s = sportInfo(m.sport);
  const playerCount = m.player_ids?.length || 0;
  return (
    <Link
      to={`/match/${m.match_id}`}
      data-testid={`upcoming-${m.match_id}`}
      className="block bg-card border border-border rounded-2xl p-3 flex items-center gap-3 hover:border-primary/40 transition-colors"
    >
      <div className="h-10 w-10 rounded-2xl flex items-center justify-center text-xl shrink-0" style={{ background: s.color + "22", border: `1px solid ${s.color}44` }}>{s.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <div className="font-bold text-sm truncate">{s.label} · {m.venue}</div>
          {isHost && <Crown className="h-3.5 w-3.5 text-primary shrink-0" />}
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{formatMatchTime(m.starts_at)}</span>
          <span>·</span>
          <span>{playerCount}/{m.max_players}</span>
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </Link>
  );
}

function SuggestedRow({ match: m }) {
  const s = sportInfo(m.sport);
  const remaining = m.max_players - (m.player_ids?.length || 0);
  return (
    <Link
      to={`/match/${m.match_id}`}
      data-testid={`suggested-${m.match_id}`}
      className="block bg-card border border-border rounded-2xl p-3 flex items-center gap-3 hover:border-primary/40 transition-colors"
    >
      <div className="h-10 w-10 rounded-2xl flex items-center justify-center text-xl shrink-0" style={{ background: s.color + "22", border: `1px solid ${s.color}44` }}>{s.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm truncate">{s.label} · {m.venue}</div>
        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
          <span>{formatMatchTime(m.starts_at)}</span>·<span>{formatRupiah(m.cost_per_player)}</span>
        </div>
      </div>
      <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-1 rounded-full shrink-0">{remaining} slot</span>
    </Link>
  );
}

function CrewChip({ team }) {
  const s = sportInfo(team.sport);
  return (
    <Link
      to={`/teams/${team.team_id}`}
      data-testid={`home-crew-${team.team_id}`}
      className="shrink-0 w-[170px] bg-card border border-border rounded-2xl p-3 hover:border-primary/40 transition-colors"
    >
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-secondary flex items-center justify-center overflow-hidden border border-border shrink-0">
          {team.logo ? <img src={team.logo} alt="" className="w-full h-full object-cover" /> : <span className="text-base">{s.icon}</span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm truncate">{team.name}</div>
          <div className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider truncate">{s.label} · {team.city}</div>
        </div>
      </div>
    </Link>
  );
}

function Skeleton({ count = 2 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="h-16 rounded-2xl bg-card border border-border animate-pulse" />
      ))}
    </div>
  );
}
